import React, { useEffect, useId, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import styles from './FollowupDrawer.module.css';

/** 待回答面板：调用方以会话和 requestId 作为 key；收起不卸载草稿、不提交审批。 */
interface FollowupDrawerProps {
  /** 现有问答/审批组件，继续使用各端原有提交协议。 */
  children: React.ReactNode;
  /** 跟随当前界面语言。 */
  language?: string;
}

/** 输入框上沿的非模态浮层，仅拖动手柄改变高度，不参与消息区布局。 */
export default function FollowupDrawer({ children, language = 'zh' }: FollowupDrawerProps) {
  const anchor = useRef<HTMLDivElement>(null);
  const grip = useRef<HTMLDivElement>(null);
  const reopen = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ y: number; height: number } | null>(null);
  const [open, setOpen] = useState(true);
  const [height, setHeight] = useState<number | null>(null);
  const [limit, setLimit] = useState(480);
  const id = useId();
  const zh = language.startsWith('zh');
  const minHeight = Math.min(220, limit);
  const actualHeight = Math.min(limit, Math.max(minHeight, height ?? Math.min(260, limit * 0.72)));

  useEffect(() => {
    /** 限制为可见视口 2/3，且不越过页面顶端；键盘/窗口缩放后同步收敛。 */
    const measure = () => {
      const viewport = window.visualViewport;
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const top = viewport?.offsetTop ?? 0;
      const available = (anchor.current?.getBoundingClientRect().top ?? viewportHeight) - top - 16;
      setLimit(Math.max(0, Math.min(Math.floor(viewportHeight * 2 / 3), available)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (anchor.current?.parentElement) observer.observe(anchor.current.parentElement);
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('scroll', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('scroll', measure);
    };
  }, []);

  /** 仅改变可见性，子组件保持挂载，所以翻页和自由输入均保留。 */
  const collapse = () => {
    drag.current = null;
    setOpen(false);
    requestAnimationFrame(() => reopen.current?.focus());
  };
  /** 鼠标、触屏和键盘共用同一高度边界。 */
  const resize = (next: number) => setHeight(Math.min(limit, Math.max(minHeight, next)));

  return (
    <div ref={anchor} className={styles.anchor} data-followup-anchor>
      {!open && (
        <button ref={reopen} type="button" className={styles.reopen} aria-expanded={false} aria-controls={id}
          onClick={() => { setOpen(true); requestAnimationFrame(() => grip.current?.focus()); }}>
          {zh ? '等待你的回答或确认 · 点击继续' : 'Waiting for your response · Continue'} ↑
        </button>
      )}
      <section id={id} className={styles.drawer} data-open={open} aria-label={zh ? '待回答与审批' : 'Pending response'}
        style={{ height: actualHeight }} hidden={!open}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !event.defaultPrevented) { event.stopPropagation(); collapse(); }
        }}>
        <div ref={grip} role="separator" tabIndex={0} aria-orientation="horizontal"
          aria-label={zh ? '拖动调整面板高度' : 'Resize response panel'}
          aria-valuemin={Math.round(minHeight)} aria-valuemax={Math.round(limit)} aria-valuenow={Math.round(actualHeight)}
          className={styles.grip}
          onPointerDown={(event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.currentTarget.focus();
            event.currentTarget.setPointerCapture(event.pointerId);
            drag.current = { y: event.clientY, height: actualHeight };
          }}
          onPointerMove={(event) => {
            if (drag.current) resize(drag.current.height + drag.current.y - event.clientY);
          }}
          onPointerUp={() => { drag.current = null; }}
          onPointerCancel={() => { drag.current = null; }}
          onLostPointerCapture={() => { drag.current = null; }}
          onKeyDown={(event) => {
            if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            resize(event.key === 'Home' ? minHeight : event.key === 'End' ? limit : actualHeight + (event.key === 'ArrowUp' ? 32 : -32));
          }}>
          <span />
        </div>
        <div className={styles.toolbar}>
          <button type="button" className={styles.close} onClick={collapse} aria-label={zh ? '收起，稍后继续' : 'Collapse and continue later'}>
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className={styles.content}>{children}</div>
      </section>
    </div>
  );
}
